import { DraftRegistrationSchema } from "@redotech/redo-model/draft-registration";
import {
  AddressSchema,
  DraftReturnSchema,
  DraftReturnSource,
} from "@redotech/redo-model/draft-return/draft-return";
import { Channel, ReturnTypeEnum } from "@redotech/redo-model/return";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const CreateDraftReturnBaseInputSchema = z.object({
  returnType: createEnum(ReturnTypeEnum),
  email: z.string().nullish(),
  address: AddressSchema.nullish(),
  referralId: z.string().nullish(),
  merchantCreated: z.boolean().nullish(),
  merchantName: z.string().nullish(),
  channel: createEnum(Channel).nullish(),
  pos: z.object({ locationId: z.string() }).nullish(),
});

export type CreateDraftReturnBaseInput = z.infer<
  typeof CreateDraftReturnBaseInputSchema
>;

export const CreateDraftReturnFromOrderInputSchema =
  CreateDraftReturnBaseInputSchema.extend({
    source: z.literal(DraftReturnSource.FROM_ORDER),
    orderId: zExt.objectId().nullish(),
    isForCreatingGiftReceipt: z.boolean().nullish(),
  });

export type CreateDraftReturnFromOrderInput = z.infer<
  typeof CreateDraftReturnFromOrderInputSchema
>;

export const CreateDraftReturnFromWarrantyRegistrationInputSchema =
  CreateDraftReturnBaseInputSchema.extend({
    source: z.literal(DraftReturnSource.FROM_WARRANTY_REGISTRATION),
    warrantyId: zExt.objectId().nullish(),
  });

export type CreateDraftReturnFromWarrantyRegistrationInput = z.infer<
  typeof CreateDraftReturnFromWarrantyRegistrationInputSchema
>;

export const CreateDraftReturnFromDraftRegistrationInputSchema =
  CreateDraftReturnBaseInputSchema.extend({
    source: z.literal(DraftReturnSource.FROM_DRAFT_REGISTRATION),
    draftRegistration: DraftRegistrationSchema,
  });

export type CreateDraftReturnFromDraftRegistrationInput = z.infer<
  typeof CreateDraftReturnFromDraftRegistrationInputSchema
>;

export const CreateDraftReturnFromProductsInputSchema =
  CreateDraftReturnBaseInputSchema.extend({
    source: z.literal(DraftReturnSource.FROM_PRODUCTS),
    productIds: z.array(z.string()),
    // All draft returns made from only products are third party claims
    // This is a temporary field to support backwards compatibility
    isThirdPartyClaim: z.boolean().optional(),
    productsBeingRecycled: z
      .array(
        z.object({ id: z.string(), quantity: z.string(), title: z.string() }),
      )
      .optional(),
  });

export type CreateDraftReturnFromProductsInput = z.infer<
  typeof CreateDraftReturnFromProductsInputSchema
>;

const CreateDraftReturnFromSourceInputSchema = z.discriminatedUnion("source", [
  CreateDraftReturnFromOrderInputSchema,
  CreateDraftReturnFromWarrantyRegistrationInputSchema,
  CreateDraftReturnFromDraftRegistrationInputSchema,
  CreateDraftReturnFromProductsInputSchema,
]);

export type CreateDraftReturnFromSourceInput = z.infer<
  typeof CreateDraftReturnFromSourceInputSchema
>;

export const createDraftReturnFromSource = {
  name: "createDraftReturnFromSource" as const,
  input: CreateDraftReturnFromSourceInputSchema,
  output: z.object({ draftReturn: DraftReturnSchema }),
};
